import React from "react";
import { toast } from "sonner";
import { Star, Flag, MapPin, BookOpen, Megaphone, HelpCircle, Wrench, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EnquiryDialog } from "@/components/discovery/EnquiryDialog";

const TYPE_STYLE = {
  "Educational": ["bg-blue-100 text-blue-700", BookOpen],
  "Service Guide": ["bg-teal-brand/10 text-teal", Wrench],
  "Vendor Update": ["bg-indigo-100 text-indigo-700", Megaphone],
  "Customer Question": ["bg-purple-100 text-purple-700", HelpCircle],
  "Verified Review": ["bg-emerald-100 text-emerald-700", Star],
  "Demo Content": ["bg-slate-100 text-slate-600", Tag],
  "Announcement": ["bg-amber-100 text-amber-800", Megaphone],
};

export const FeedItemCard = ({ item }) => {
  const [style, Icon] = TYPE_STYLE[item.content_type] || TYPE_STYLE["Educational"];
  const isReview = item.content_type === "Verified Review";
  const report = () => toast.success("Thank you — this content has been flagged for review.");

  return (
    <div className={`rounded-2xl border bg-white p-4 shadow-sm ${isReview ? "border-emerald-200" : "border-slate-200"}`} data-testid={`feed-item-${item.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${style}`}><Icon className="h-3 w-3" /> {item.content_type}</span>
        {item.subcategory_slug && <span className="rounded-full bg-slate-100 text-slate-600 px-2 py-0.5 text-[11px]">{item.subcategory_slug.replace(/-/g, " ")}</span>}
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${item.verification_status === "Verified" ? "bg-emerald-600 text-white" : item.verification_status === "Demo" ? "bg-amber-500 text-white" : "bg-slate-200 text-slate-600"}`}>{item.verification_status}</span>
      </div>

      {item.demo_label && (
        <p className="mt-2 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block">{item.demo_label}</p>
      )}

      {isReview ? (
        <div className="mt-2">
          <div className="flex items-center gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`h-4 w-4 ${i < (item.rating || 5) ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />)}</div>
          <p className="text-sm text-slate-700 mt-1 italic">“{item.description}”</p>
        </div>
      ) : (
        <>
          <h3 className="font-semibold text-slate-900 mt-2">{item.title}</h3>
          {item.description && <p className="text-sm text-slate-600 mt-1">{item.description}</p>}
        </>
      )}

      <div className="mt-3 flex items-center justify-between">
        <p className="text-[11px] text-slate-400 flex items-center gap-1">
          {item.vendor_name && <span className="font-medium text-slate-500">{item.vendor_name}</span>}
          {item.city && <><MapPin className="h-3 w-3" /> {item.city}</>}
          <span>· {(item.date || "").slice(0, 10)}</span>
        </p>
        <div className="flex items-center gap-1">
          <EnquiryDialog categorySlug={item.category_slug} subcategorySlug={item.subcategory_slug}
            trigger={<Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`feed-enquiry-${item.id}`}>Enquire</Button>} />
          <button onClick={report} className="p-1.5 text-slate-400 hover:text-rose-500" title="Report content" data-testid={`feed-report-${item.id}`}><Flag className="h-3.5 w-3.5" /></button>
        </div>
      </div>
    </div>
  );
};
